import './AutoCompleteTextInput.scss';

import type { InputRef } from 'antd';
import { AutoComplete, Input } from 'antd';
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator';
import React from 'react';
import type { RegisterOptions } from 'react-hook-form';
import { Controller, useFormContext } from 'react-hook-form';
import { useDebouncedCallback } from 'use-debounce';

import type { InputNoteConfig } from '../_common';
import {
  getHookFieldClass,
  getHookGroupClass,
  renderErrorMessage,
  renderHookInputHeader
} from '../_common';
import type { SelectItemWithDescription } from './SelectInput';

interface AutoCompleteTextInputProps {
  label?: string;
  inputNote?: InputNoteConfig;
  name: string;
  required?: boolean;
  options?: RegisterOptions;
  addon?: React.ReactNode;
  autocompleteFooter?: React.ReactNode;
  open?: boolean;
  placeholder?: string;
  disabled?: boolean;
  customOnBlur?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>) => void;
  onSearch: (value: string) => void;
  onSelect: (id: string) => void;
  items: SelectItemWithDescription[];
  notFoundText?: React.ReactNode;
  isSearching?: boolean;
  minRows?: number; // if set, input will be TextArea
  hideLabel?: boolean;
}

const AutoCompleteTextInput = React.forwardRef<InputRef, AutoCompleteTextInputProps>(
  (
    {
      name,
      inputNote,
      label,
      required,
      options,
      addon,
      autocompleteFooter,
      open,
      placeholder,
      disabled,
      onSearch,
      customOnBlur,
      onSelect,
      items,
      notFoundText,
      isSearching,
      minRows,
      hideLabel
    },
    ref
  ) => {
    const { control, getFieldState, formState } = useFormContext();

    const { invalid, isDirty, error, isTouched } = getFieldState(name, formState);

    const showError = !!error && isDirty;

    const handleSearch = useDebouncedCallback((value) => onSearch(value), 1000);

    return (
      <div
        className={getHookGroupClass(label, `AutoCompleteTextInput${addon ? ' with-addon' : ''}`)}
      >
        {!hideLabel && renderHookInputHeader(name, label, required, inputNote)}
        <Controller
          name={name}
          control={control}
          rules={options}
          render={({ field }) => (
            <div className="AutoCompleteTextInput_wrapper">
              <AutoComplete
                open={open}
                showSearch={{ onSearch: handleSearch }}
                onSelect={onSelect}
                options={items.map((option) => ({
                  value: option.id,
                  label: (
                    <div className="AutoCompleteTextInput_option">
                      <div className="AutoCompleteTextInput_optionName">{option.name}</div>
                      {option.description && (
                        <div
                          className="AutoCompleteTextInput_optionDescription"
                          title={option.description.toString()}
                        >
                          {option.description}
                        </div>
                      )}
                    </div>
                  )
                }))}
                className={`AutoComplete ${addon ? 'withAddon' : ''}`}
                value={field.value}
                notFoundContent={isSearching ? <LoadingIndicator /> : notFoundText}
                popupRender={(menus) => (
                  <div className="AutoCompleteTextInput_customDropdown">
                    {menus}
                    {autocompleteFooter && (
                      <div className="AutoComplete_customDropdownFooter">{autocompleteFooter}</div>
                    )}
                  </div>
                )}
              >
                {minRows ? (
                  <Input.TextArea
                    {...field}
                    id={name}
                    placeholder={placeholder}
                    disabled={disabled}
                    className={getHookFieldClass(isTouched, invalid)}
                    autoComplete="off"
                    ref={ref}
                    onBlur={(e) => {
                      if (customOnBlur) customOnBlur(e);
                      field.onBlur();
                    }}
                    autoSize={{ minRows: minRows || 4, maxRows: 10 }}
                  />
                ) : (
                  <Input
                    {...field}
                    id={name}
                    placeholder={placeholder}
                    disabled={disabled}
                    className={getHookFieldClass(isTouched, invalid)}
                    autoComplete="off"
                    ref={ref}
                    onBlur={(e) => {
                      if (customOnBlur) customOnBlur(e);
                      field.onBlur();
                    }}
                  />
                )}
              </AutoComplete>
              <div className="AutoCompleteTextInput_inputAddon">{addon}</div>
            </div>
          )}
        />
        {renderErrorMessage(showError, error?.message)}
      </div>
    );
  }
);

export default AutoCompleteTextInput;
